import { useEffect, useState } from "react";
import api, { apiError, fmtUSD } from "@/lib/api";
import { Panel, Metric, Table, Empty, StatusBadge } from "@/components/terminal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { Newspaper, RefreshCw, ShieldAlert } from "lucide-react";

const REC_CLS = {
  "ACCUMULATE": "bg-emerald-500/10 text-emerald-400 border-emerald-800/60",
  "NORMAL": "bg-blue-500/10 text-blue-400 border-blue-800/60",
  "HOLD": "bg-slate-500/10 text-slate-300 border-slate-600/60",
  "REVIEW": "bg-amber-500/10 text-amber-400 border-amber-800/60",
  "REDUCE DEPLOYMENT": "bg-orange-500/10 text-orange-400 border-orange-800/60",
  "PAUSE NEW PURCHASES": "bg-red-500/10 text-red-400 border-red-800/60",
};
const SENT_CLS = (s) => (s > 20 ? "text-emerald-400" : s < -20 ? "text-red-400" : "text-slate-400");
const EVENT_TYPES = ["REGULATION", "SECURITY", "EXPLOIT", "EXCHANGE", "MACRO", "INTEREST_RATE", "INFLATION", "EMPLOYMENT", "ETF", "INSTITUTIONAL", "LIQUIDITY", "BANKRUPTCY", "TOKEN_UNLOCK", "TOKENOMICS", "PROTOCOL_UPGRADE", "DELISTING", "LISTING", "CORPORATE", "EARNINGS", "LEGAL", "GEOPOLITICAL", "PARTNERSHIP", "ADOPTION", "OTHER"];

function EventRow({ e }) {
  return (
    <tr className="hover:bg-[#222630]" data-testid={`intel-event-${e.id.slice(0, 8)}`}>
      <td className="td-cell font-mono whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
      <td className="td-cell max-w-[380px]">
        <a href={e.source_url} target="_blank" rel="noreferrer" className="text-slate-200 hover:text-[#D4AF37] leading-snug">{e.headline}</a>
        <div className="text-[10px] text-slate-500 mt-0.5">{e.source} · {e.source_quality}{e.corroborating_sources > 1 ? ` · ${e.corroborating_sources} sources` : ""}{e.needs_human_review ? " · NEEDS HUMAN REVIEW" : ""}</div>
      </td>
      <td className="td-cell">{e.event_type.replace(/_/g, " ")}</td>
      <td className="td-cell">{(e.assets_affected || []).join(", ") || "—"}</td>
      <td className={`td-num ${SENT_CLS(e.sentiment)}`}>{e.sentiment > 0 ? "+" : ""}{e.sentiment}</td>
      <td className="td-num">{e.impact}</td>
      <td className="td-num">{e.confidence}</td>
      <td className="td-cell"><StatusBadge status={e.verification_status} /></td>
    </tr>
  );
}

export default function Intelligence() {
  const [d, setD] = useState(null);
  const [events, setEvents] = useState([]);
  const [filters, setFilters] = useState({ asset: "", event_type: "", min_impact: "", verification: "" });
  const [assetDetail, setAssetDetail] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState("BTC");
  const [manual, setManual] = useState({ headline: "", event_type: "MACRO", assets_affected: "", sentiment: 0, impact: 50, confidence: 70, verified: false, event_date: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = () => {
    setError(null);
    api.get("/intelligence").then((r) => setD(r.data)).catch(() => setError("Intelligence feed unavailable."));
    loadEvents();
  };
  const loadEvents = (f = filters) => {
    const params = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v && params.set(k, v));
    api.get(`/intelligence/events?${params}`).then((r) => setEvents(r.data)).catch(() => {});
  };
  const loadAsset = (sym) => {
    setSelectedAsset(sym);
    api.get(`/intelligence/asset/${sym}`).then((r) => setAssetDetail(r.data)).catch(() => {});
  };
  useEffect(() => { load(); loadAsset("BTC"); }, []); // eslint-disable-line

  const ingest = async () => {
    setBusy(true);
    try {
      const r = await api.post("/intelligence/ingest");
      toast.success(`Ingested ${r.data.new_events} new event(s)`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const resolveCritical = async (id) => {
    try { await api.post(`/intelligence/critical/${id}/resolve`); toast.success("Critical event resolved"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const submitManual = async (e) => {
    e.preventDefault();
    try {
      await api.post("/intelligence/events/manual", {
        ...manual,
        assets_affected: manual.assets_affected.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean),
        sentiment: parseInt(manual.sentiment) || 0, impact: parseInt(manual.impact) || 0,
        confidence: parseInt(manual.confidence) || 0,
        event_date: manual.event_date || null,
      });
      toast.success("Event recorded");
      setManual({ ...manual, headline: "" });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  if (!d) {
    if (error) return (
      <div className="panel p-6" data-testid="intel-error">
        <div className="text-red-400 text-sm mb-3">{error}</div>
        <button data-testid="intel-retry" onClick={load} className="text-xs border border-[#D4AF37]/50 text-[#D4AF37] rounded-sm px-3 py-1.5">RETRY</button>
      </div>
    );
    return <div className="text-slate-500 font-mono text-sm" data-testid="intel-loading">Loading intelligence…</div>;
  }
  const assets = d.assets || {};

  return (
    <div className="space-y-5" data-testid="intelligence-page">
      <div className="flex flex-wrap items-center gap-3">
        <Newspaper className="w-5 h-5 gold-text" />
        <h2 className="font-display text-xl font-bold">Treasury Intelligence</h2>
        <span className="text-[11px] text-slate-500">Signals inform decisions — they never execute trades. Risk Governor retains final authority.</span>
        <Button data-testid="intel-ingest-button" onClick={ingest} disabled={busy} size="sm" variant="outline" className="ml-auto border-[#D4AF37]/50 text-[#D4AF37]">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} />{busy ? "SCANNING…" : "SCAN SOURCES"}
        </Button>
      </div>

      {d.critical_events.length > 0 && (
        <div className="panel border-red-700 bg-red-950/30 px-4 py-3 space-y-2" data-testid="critical-events-banner">
          {d.critical_events.map((c) => (
            <div key={c.id} className="flex items-center gap-3">
              <ShieldAlert className="w-5 h-5 text-red-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-red-300 text-sm font-semibold truncate">{c.title}</div>
                <div className="text-[10px] text-red-400/70">CRITICAL EVENT — purchases paused for {c.assets.join(", ")} · impact {c.impact} · confidence {c.confidence}</div>
              </div>
              <Button size="sm" variant="outline" data-testid={`resolve-critical-${c.id.slice(0, 8)}`} onClick={() => resolveCritical(c.id)} className="border-red-700 text-red-300 h-7 text-[11px]">RESOLVE</Button>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Metric label="Market Regime" value={d.market_regime} tone="gold" testId="intel-regime" />
        <Metric label="Global News Risk" value={`${d.global_news_risk}/100`} tone={d.global_news_risk >= 60 ? "neg" : undefined} testId="intel-global-risk" />
        <Metric label="Global Opportunity" value={`${d.global_opportunity}/100`} tone={d.global_opportunity >= 60 ? "pos" : undefined} testId="intel-global-opp" />
        <Metric label="BTC News Risk" value={`${assets.BTC?.news_risk ?? 0}/100`} testId="intel-btc-risk" />
        <Metric label="SOL News Risk" value={`${assets.SOL?.news_risk ?? 0}/100`} testId="intel-sol-risk" />
        <Metric label="Wildcard News Risk" value={`${d.wildcard_news_risk}/100`} testId="intel-wildcard-risk" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {["BTC", "XRP", "SOL", "HBAR", "XLM", "ALGO", "ZBCN", "CRO"].map((sym) => {
          const b = assets[sym] || {};
          return (
            <button key={sym} data-testid={`intel-asset-card-${sym}`} onClick={() => loadAsset(sym)}
              className={`panel p-4 text-left transition-colors hover:border-[#3F4656] ${selectedAsset === sym ? "border-[#D4AF37]/50" : ""}`}>
              <div className="flex items-center justify-between">
                <span className="font-display font-bold">{sym}</span>
                <span className={`badge ${REC_CLS[b.recommendation] || REC_CLS.HOLD}`} data-testid={`intel-rec-${sym}`}>{b.recommendation || "HOLD"}</span>
              </div>
              <div className="grid grid-cols-2 gap-1 mt-3 text-[11px] text-slate-500">
                <span>News Risk <span className="font-mono text-slate-200 float-right">{b.news_risk == null ? "—" : `${b.news_risk}/100`}</span></span>
                <span>Opportunity <span className="font-mono text-slate-200 float-right">{b.news_opportunity == null ? "—" : `${b.news_opportunity}/100`}</span></span>
                <span>Sentiment <span className={`font-mono float-right ${SENT_CLS(b.sentiment_avg)}`}>{b.sentiment_avg > 0 ? "+" : ""}{b.sentiment_avg}</span></span>
                <span>Events 7d/30d <span className="font-mono text-slate-200 float-right">{b.events_7d}/{b.events_30d}</span></span>
              </div>
              {b.critical_event && <div className="text-[10px] text-red-400 mt-2 font-semibold">CRITICAL EVENT ACTIVE — purchases paused</div>}
            </button>
          );
        })}
      </div>

      {assetDetail && (
        <Panel title={`${selectedAsset} Asset Intelligence`} testId="asset-intelligence-panel">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm mb-4">
            <div><div className="metric-label">Price</div><div className="font-mono text-lg text-slate-100">{fmtUSD(assetDetail.price?.price)}</div></div>
            <div><div className="metric-label">Portfolio Weight</div><div className="font-mono text-lg text-slate-100">{assetDetail.weight_pct}%</div></div>
            <div><div className="metric-label">Deployment Score</div><div className="font-mono text-lg text-slate-100">{assetDetail.deployment?.deployment_score ?? "—"}</div></div>
            <div><div className="metric-label">Recommendation</div><span className={`badge ${REC_CLS[assetDetail.brief?.recommendation] || REC_CLS.HOLD}`} data-testid="asset-rec">{assetDetail.brief?.recommendation}</span></div>
            <div><div className="metric-label">30d Change</div><div className="font-mono text-lg">{assetDetail.price?.change_30d != null ? `${(assetDetail.price.change_30d * 100).toFixed(1)}%` : "—"}</div></div>
          </div>
          {assetDetail.warnings?.length > 0 && (
            <div className="mb-4">
              <div className="metric-label mb-2">Upcoming / Active Risks</div>
              {assetDetail.warnings.map((w, i) => (
                <div key={i} className="text-xs text-amber-300 border-l-2 border-amber-600 pl-3 py-1">{w.event_type.replace(/_/g, " ")} — {w.headline} <span className="text-slate-500">(impact {w.impact}, {w.verification_status.toLowerCase()})</span></div>
              ))}
            </div>
          )}
          {assetDetail.upcoming?.length > 0 && (
            <div className="mb-4">
              <div className="metric-label mb-2">Upcoming Catalysts</div>
              {assetDetail.upcoming.map((u) => (
                <div key={u.id} className="text-xs text-slate-300 py-1">{new Date(u.event_date).toLocaleDateString()} — {u.headline}</div>
              ))}
            </div>
          )}
          <div className="metric-label mb-2">Recent Events</div>
          {assetDetail.events.length === 0 ? <Empty>No recent events for {selectedAsset}.</Empty> : (
            <div className="space-y-1.5">
              {assetDetail.events.slice(0, 8).map((e) => (
                <div key={e.id} className="flex items-center gap-3 text-xs border-b border-[#222630] pb-1.5">
                  <span className={`font-mono w-10 text-right ${SENT_CLS(e.sentiment)}`}>{e.sentiment > 0 ? "+" : ""}{e.sentiment}</span>
                  <span className="text-slate-300 flex-1 truncate">{e.headline}</span>
                  <span className="text-slate-500">{e.event_type.replace(/_/g, " ")}</span>
                  <StatusBadge status={e.verification_status} />
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}

      {d.divergences.length > 0 && (
        <Panel title="Market Reaction Divergences" testId="divergences-panel">
          <div className="space-y-2">
            {d.divergences.map((dv, i) => (
              <div key={i} className="flex items-center gap-3 text-xs border-b border-[#222630] pb-2">
                <span className="badge bg-blue-500/10 text-blue-400 border-blue-800/60">{dv.divergence.replace(/_/g, " ")}</span>
                <span className="font-semibold">{dv.asset}</span>
                <span className="text-slate-400 flex-1 truncate">{dv.headline}</span>
                <span className="font-mono">sent {dv.sentiment} / price {dv.price_change_pct > 0 ? "+" : ""}{dv.price_change_pct}%</span>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-slate-600 mt-2">Divergence is one input among many; it never triggers execution.</p>
        </Panel>
      )}

      <Panel title="Event Stream" testId="intel-events-panel"
        right={<span className="text-[10px] text-slate-500">{d.last_ingest ? `Last scan ${new Date(d.last_ingest).toLocaleTimeString()}` : ""}</span>}>
        <div className="flex flex-wrap gap-2 mb-3">
          <Select value={filters.asset || "ALL"} onValueChange={(v) => { const f = { ...filters, asset: v === "ALL" ? "" : v }; setFilters(f); loadEvents(f); }}>
            <SelectTrigger data-testid="filter-asset" className="w-28 h-8 bg-[#121316] border-[#2D323E] text-xs"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#17191E] border-[#2D323E]">
              <SelectItem value="ALL">All assets</SelectItem>
              {["BTC", "XRP", "SOL", "HBAR", "XLM", "ALGO", "ZBCN", "CRO"].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.event_type || "ALL"} onValueChange={(v) => { const f = { ...filters, event_type: v === "ALL" ? "" : v }; setFilters(f); loadEvents(f); }}>
            <SelectTrigger data-testid="filter-type" className="w-40 h-8 bg-[#121316] border-[#2D323E] text-xs"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#17191E] border-[#2D323E]">
              <SelectItem value="ALL">All types</SelectItem>
              {EVENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid="filter-min-impact" type="number" min="0" max="100" placeholder="min impact" value={filters.min_impact}
            onChange={(e) => { const f = { ...filters, min_impact: e.target.value }; setFilters(f); }}
            onKeyDown={(e) => e.key === "Enter" && loadEvents()}
            onBlur={() => loadEvents()} className="w-28 h-8 bg-[#121316] border-[#2D323E] text-xs font-mono" />
          <Select value={filters.verification || "ALL"} onValueChange={(v) => { const f = { ...filters, verification: v === "ALL" ? "" : v }; setFilters(f); loadEvents(f); }}>
            <SelectTrigger data-testid="filter-verification" className="w-36 h-8 bg-[#121316] border-[#2D323E] text-xs"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#17191E] border-[#2D323E]">
              <SelectItem value="ALL">Any status</SelectItem>
              {["CONFIRMED", "CORROBORATED", "UNVERIFIED"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {events.length === 0 ? <Empty>No events match. Sources are scanned automatically every 15 minutes.</Empty> : (
          <Table testId="intel-events-table" head={[{ label: "Time" }, { label: "Headline" }, { label: "Type" }, { label: "Assets" }, { label: "Sent.", num: true }, { label: "Impact", num: true }, { label: "Conf.", num: true }, { label: "Verification" }]}>
            {events.map((e) => <EventRow key={e.id} e={e} />)}
          </Table>
        )}
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Source Status" testId="intel-feeds-panel">
          {(d.feeds || []).map((f) => (
            <div key={f.id} className="flex items-center justify-between border-b border-[#222630] py-2 text-sm">
              <span className="text-slate-300">{f.name}</span>
              <span className="text-[10px] text-slate-500">{f.items != null ? `${f.items} items, ${f.new} new` : (f.error || "")}</span>
              <StatusBadge status={f.status} />
            </div>
          ))}
          <p className="text-[10px] text-slate-600 mt-3">Provider architecture supports News / Regulatory / Macro / Security / Market-Event providers. Additional feeds are configuration, not code.</p>
        </Panel>
        <Panel title="Record Event Manually (macro calendar, unlocks, verified alerts)" testId="manual-event-panel">
          <form onSubmit={submitManual} className="grid grid-cols-2 gap-2.5">
            <div className="col-span-2"><Input data-testid="manual-headline" required placeholder="Headline" value={manual.headline} onChange={(e) => setManual({ ...manual, headline: e.target.value })} className="bg-[#121316] border-[#2D323E] h-8 text-xs" /></div>
            <Select value={manual.event_type} onValueChange={(v) => setManual({ ...manual, event_type: v })}>
              <SelectTrigger data-testid="manual-type" className="h-8 bg-[#121316] border-[#2D323E] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#17191E] border-[#2D323E]">{EVENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="manual-assets" placeholder="Assets (BTC, XRP)" value={manual.assets_affected} onChange={(e) => setManual({ ...manual, assets_affected: e.target.value })} className="bg-[#121316] border-[#2D323E] h-8 text-xs font-mono" />
            <Input data-testid="manual-sentiment" type="number" min="-100" max="100" placeholder="sentiment -100..100" value={manual.sentiment} onChange={(e) => setManual({ ...manual, sentiment: e.target.value })} className="bg-[#121316] border-[#2D323E] h-8 text-xs font-mono" />
            <Input data-testid="manual-impact" type="number" min="0" max="100" placeholder="impact 0-100" value={manual.impact} onChange={(e) => setManual({ ...manual, impact: e.target.value })} className="bg-[#121316] border-[#2D323E] h-8 text-xs font-mono" />
            <Input data-testid="manual-confidence" type="number" min="0" max="100" placeholder="confidence 0-100" value={manual.confidence} onChange={(e) => setManual({ ...manual, confidence: e.target.value })} className="bg-[#121316] border-[#2D323E] h-8 text-xs font-mono" />
            <Input data-testid="manual-date" type="date" value={manual.event_date} onChange={(e) => setManual({ ...manual, event_date: e.target.value })} className="bg-[#121316] border-[#2D323E] h-8 text-xs font-mono" />
            <label className="col-span-2 flex items-center gap-2 text-xs text-slate-400">
              <input type="checkbox" data-testid="manual-verified" checked={manual.verified} onChange={(e) => setManual({ ...manual, verified: e.target.checked })} /> Verified against official source
            </label>
            <Button data-testid="manual-submit" type="submit" className="col-span-2 bg-[#D4AF37] hover:bg-[#C5A059] text-[#0B0C0E] font-bold h-8 text-xs">RECORD EVENT</Button>
          </form>
        </Panel>
      </div>
    </div>
  );
}
